import React from "react";
import { m, LazyMotion, domAnimation } from "framer-motion";
import { cn } from "@heroui/theme";

function CheckIcon(props) {
  return (
    <svg {...props} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <m.path
        animate={{ pathLength: 1 }}
        d="M5 13l4 4L19 7"
        initial={{ pathLength: 0 }}
        strokeLinecap="round"
        strokeLinejoin="round"
        transition={{ delay: 0.2, type: "tween", ease: "easeOut", duration: 0.3 }}
      />
    </svg>
  );
}

const RowSteps = React.forwardRef(
  (
    {
      color = "primary",
      steps = [],
      currentStep = 0,          // <- controlled index (0-based)
      hideProgressBars = false,
      stepClassName,
      className,
      ...rest                   // <- spread ONLY on <nav>, not on <button>
    },
    ref
  ) => {
    const colors = React.useMemo(() => {
      let userColor, fgColor;
      const colorsVars = [
        "[--active-fg-color:var(--step-fg-color)]",
        "[--active-border-color:var(--step-color)]",
        "[--active-color:var(--step-color)]",
        "[--complete-background-color:var(--step-color)]",
        "[--complete-border-color:var(--step-color)]",
        "[--inactive-border-color:hsl(var(--heroui-default-300))]",
        "[--inactive-color:hsl(var(--heroui-default-300))]",
      ];

      switch (color) {
        case "secondary":
          userColor = "[--step-color:hsl(var(--heroui-secondary))]";
          fgColor = "[--step-fg-color:hsl(var(--heroui-secondary-foreground))]";
          break;
        case "success":
          userColor = "[--step-color:hsl(var(--heroui-success))]";
          fgColor = "[--step-fg-color:hsl(var(--heroui-success-foreground))]";
          break;
        case "warning":
          userColor = "[--step-color:hsl(var(--heroui-warning))]";
          fgColor = "[--step-fg-color:hsl(var(--heroui-warning-foreground))]";
          break;
        case "danger":
          userColor = "[--step-color:hsl(var(--heroui-error))]";
          fgColor = "[--step-fg-color:hsl(var(--heroui-error-foreground))]";
          break;
        case "default":
          userColor = "[--step-color:hsl(var(--heroui-default))]";
          fgColor = "[--step-fg-color:hsl(var(--heroui-default-foreground))]";
          break;
        default:
          userColor = "[--step-color:hsl(var(--heroui-primary))]";
          fgColor = "[--step-fg-color:hsl(var(--heroui-primary-foreground))]";
          break;
      }

      if (!className?.includes("--step-fg-color")) colorsVars.unshift(fgColor);
      if (!className?.includes("--step-color")) colorsVars.unshift(userColor);
      if (!className?.includes("--inactive-bar-color"))
        colorsVars.push("[--inactive-bar-color:hsl(var(--heroui-default-300))]");

      return colorsVars;
    }, [color, className]);

    return (
      <nav aria-label="Progress" className="-my-2 w-full py-2" {...rest}>
        <ol className={cn("flex w-full items-center gap-x-2 px-1", colors, className)}>
          {steps?.map((step, stepIdx) => {
            const status =
              currentStep === stepIdx ? "active" :
              currentStep < stepIdx ? "inactive" : "complete";

            return (
              <li key={stepIdx} className="flex flex-1 items-center">
                {/* Not clickable; just a visual pill */}
                <div
                  ref={ref}
                  aria-current={status === "active" ? "step" : undefined}
                  className={cn(
                    "group relative z-10 flex w-full items-center justify-center gap-2 py-1.5",
                    stepClassName
                  )}
                >
                  <LazyMotion features={domAnimation}>
                    <m.div animate={status} className="relative">
                      <m.div
                        className={cn(
                          "border-medium text-sm flex h-7 w-7 items-center justify-center rounded-full font-semibold",
                          { "shadow-lg": status === "complete" }
                        )}
                        initial={false}
                        transition={{ duration: 0.25 }}
                        variants={{
                          inactive: {
                            backgroundColor: "transparent",
                            borderColor: "var(--inactive-border-color)",
                            color: "var(--inactive-color)",
                          },
                          active: {
                            backgroundColor: "transparent",
                            borderColor: "var(--active-border-color)",
                            color: "var(--active-color)",
                          },
                          complete: {
                            backgroundColor: "var(--complete-background-color)",
                            borderColor: "var(--complete-border-color)",
                            color: "var(--active-fg-color)",
                          },
                        }}
                      >
                        {status === "complete" ? (
                          <CheckIcon className="h-5 w-5 text-[var(--active-fg-color)]" />
                        ) : (
                          <span>{stepIdx + 1}</span>
                        )}
                      </m.div>
                    </m.div>
                  </LazyMotion>

                  <div className="max-w-full">
                    <div
                      className={cn(
                        "text-xs md:text-sm font-medium transition-[color,opacity] duration-300 group-active:opacity-80",
                        { "text-default-500": status === "inactive" }
                      )}
                    >
                      {step.title}
                    </div>
                  </div>
                </div>

                {!hideProgressBars && stepIdx < steps.length - 1 ? (
                  <div className="flex-1 px-1">
                    <div className="relative h-0.5 w-full">
                      <div className="absolute inset-0 bg-[var(--inactive-bar-color)]" />
                      <div
                        className={cn(
                          "absolute inset-y-0 left-0 w-0 bg-[var(--active-border-color)] transition-[width] duration-300",
                          { "w-full": stepIdx < currentStep }
                        )}
                      />
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }
);

RowSteps.displayName = "RowSteps";
export default RowSteps;